import axios from 'axios';
import { useDemoStore } from '@/store/demoStore';
import * as demoService from './demoService';

// Create axios instance with base URL from environment variables
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://fttf-world-api.bharath-f04.workers.dev',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Service wrapper that checks if demo mode is active
const useApiService = () => {
  const { isDemoMode } = useDemoStore();

  // User services
  const getUser = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserProfile(userId);
    }
    const response = await api.get(`/api/users/${userId}`);
    return response.data.user;
  };

  const getCurrentUser = async () => {
    if (isDemoMode) {
      return demoService.getDemoUser();
    }
    const response = await api.get('/api/auth/profile');
    return response.data.user;
  };

  // Stream services
  const getStreams = async () => {
    if (isDemoMode) {
      return demoService.getDemoStreams();
    }
    const response = await api.get('/api/streams');
    return response.data.streams;
  };

  const getStream = async (streamId: string) => {
    if (isDemoMode) {
      return demoService.getDemoStream(streamId);
    }
    const response = await api.get(`/api/streams/${streamId}`);
    return response.data.stream;
  };

  const getUserStreams = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserStreams(userId);
    }
    const response = await api.get(`/api/streams/creator/${userId}`);
    return response.data.streams;
  };

  // Drop services
  const getDrops = async () => {
    if (isDemoMode) {
      return demoService.getDemoDrops();
    }
    const response = await api.get('/api/drops');
    return response.data.drops;
  };

  const getDrop = async (dropId: string) => {
    if (isDemoMode) {
      return demoService.getDemoDrop(dropId);
    }
    const response = await api.get(`/api/drops/${dropId}`);
    return response.data.drop;
  };

  const getUserDrops = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserDrops(userId);
    }
    const response = await api.get(`/api/drops/creator/${userId}`);
    return response.data.drops;
  };

  // NFT services
  const getNfts = async () => {
    if (isDemoMode) {
      return demoService.getDemoNfts();
    }
    const response = await api.get('/api/nfts');
    return response.data.nfts;
  };

  const getNft = async (nftId: string) => {
    if (isDemoMode) {
      return demoService.getDemoNft(nftId);
    }
    const response = await api.get(`/api/nfts/${nftId}`);
    return response.data.nft;
  };

  const getUserNfts = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserNfts(userId);
    }
    const response = await api.get(`/api/nfts/owner/${userId}`);
    return response.data.nfts;
  };

  const getDropNfts = async (dropId: string) => {
    if (isDemoMode) {
      return demoService.getDemoDropNfts(dropId);
    }
    const response = await api.get(`/api/nfts/drop/${dropId}`);
    return response.data.nfts;
  };

  // Marketplace services
  const getMarketplaceListings = async () => {
    if (isDemoMode) {
      return demoService.getDemoMarketplaceListings();
    }
    const response = await api.get('/api/marketplace');
    return response.data.listings;
  };

  const getMarketplaceListing = async (listingId: string) => {
    if (isDemoMode) {
      return demoService.getDemoMarketplaceListing(listingId);
    }
    const response = await api.get(`/api/marketplace/${listingId}`);
    return response.data.listing;
  };

  const getUserListings = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserListings(userId);
    }
    const response = await api.get(`/api/marketplace/seller/${userId}`);
    return response.data.listings;
  };

  // Sales services
  const getSales = async () => {
    if (isDemoMode) {
      return demoService.getDemoSales();
    }
    const response = await api.get('/api/marketplace/sales');
    return response.data.sales;
  };

  const getUserSales = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserSales(userId);
    }
    const response = await api.get(`/api/marketplace/sales/user/${userId}`);
    return response.data.sales;
  };

  // Tip services
  const getTips = async () => {
    if (isDemoMode) {
      return demoService.getDemoTips();
    }
    const response = await api.get('/api/tipping');
    return response.data.tips;
  };

  const getUserReceivedTips = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserReceivedTips(userId);
    }
    const response = await api.get(`/api/tipping/received/${userId}`);
    return response.data.tips;
  };

  const getUserSentTips = async (userId: string) => {
    if (isDemoMode) {
      return demoService.getDemoUserSentTips(userId);
    }
    const response = await api.get(`/api/tipping/sent/${userId}`);
    return response.data.tips;
  };

  // Governance services
  const getProposals = async () => {
    if (isDemoMode) {
      return demoService.getDemoProposals();
    }
    const response = await api.get('/api/governance/proposals');
    return response.data.proposals;
  };

  const getProposal = async (proposalId: string) => {
    if (isDemoMode) {
      return demoService.getDemoProposal(proposalId);
    }
    const response = await api.get(`/api/governance/proposals/${proposalId}`);
    return response.data.proposal;
  };

  const getTreasury = async () => {
    if (isDemoMode) {
      return demoService.getDemoTreasury();
    }
    const response = await api.get('/api/governance/treasury');
    return response.data.treasury;
  };

  // Analytics services
  const getAnalytics = async () => {
    if (isDemoMode) {
      return demoService.getDemoAnalytics();
    }
    const response = await api.get('/api/admin/analytics');
    return response.data.analytics;
  };

  // Create drop
  const createDrop = async (dropData: any) => {
    if (isDemoMode) {
      return { id: 'demo-drop-' + Math.floor(Math.random() * 1000), ...dropData };
    }
    const response = await api.post('/api/drops', dropData);
    return response.data.drop;
  };

  // Create NFT
  const createNFT = async (nftData: any) => {
    if (isDemoMode) {
      return { id: 'demo-nft-' + Math.floor(Math.random() * 1000), ...nftData };
    }
    const response = await api.post('/api/nfts', nftData);
    return response.data.nft;
  };

  // Update NFT
  const updateNFT = async (nftId: string, updateData: any) => {
    if (isDemoMode) {
      return { id: nftId, ...updateData };
    }
    const response = await api.put(`/api/nfts/${nftId}`, updateData);
    return response.data.nft;
  };

  return {
    // User services
    getUser,
    getCurrentUser,
    
    // Stream services
    getStreams,
    getStream,
    getUserStreams,
    
    // Drop services
    getDrops,
    getDrop,
    getUserDrops,
    createDrop,
    
    // NFT services
    getNfts,
    getNft,
    getUserNfts,
    getDropNfts,
    createNFT,
    updateNFT,
    
    // Marketplace services
    getMarketplaceListings,
    getMarketplaceListing,
    getUserListings,
    
    // Sales services
    getSales,
    getUserSales,
    
    // Tip services
    getTips,
    getUserReceivedTips,
    getUserSentTips,
    
    // Governance services
    getProposals,
    getProposal,
    getTreasury,
    
    // Analytics services
    getAnalytics,
  };
};

export default useApiService;